import { useMemo, useState } from 'react';
import { promptDialog, confirmDialog } from '../../utils/dialog';
import {
    FieldGroupAttrs,
    Layout,
    LayoutTab,
    UNPLACED_TAB,
    moveSection as moveSectionIn,
    moveTab as moveTabIn,
    sectionKey,
} from '../../utils/fieldGroups';

// 字段清单的「页签 → 分组」编辑逻辑 (2026-10-23 从参数模板编辑器抽出):
// 参数模板 (SchemaEditModal) 与设备字段组 (FieldGroupEditorModal) 共用 —— 布局显式存 (utils/fieldGroups),
// 字段用 tab/sec 挂进去, 字段跟着分组走: 在哪个分组点「+ 添加字段」就落在哪.

const norm = (v?: string) => (v || '').trim();
const sameGroup = (a: FieldGroupAttrs, b: FieldGroupAttrs) => norm(a.tab) === norm(b.tab) && norm(a.sec) === norm(b.sec);

export interface LayoutEditorOptions<F extends FieldGroupAttrs> {
    fields: F[];
    setFields: (next: F[]) => void;
    /** 新建一个空字段 (分组模式下带上落点) */
    makeField: (tab?: string, sec?: string) => F;
    /** 校验类错误提示 (重名 / 保留名) */
    setError: (msg: string) => void;
}

export interface LayoutEditor<F extends FieldGroupAttrs> {
    layout: Layout;
    setLayout: (l: Layout) => void;
    /** 启用了分组 */
    grouped: boolean;
    activeTab: string;
    setActiveTab: (t: string) => void;
    focusTab?: string;
    openSection?: { key: string };
    /** 当前页签在布局里的位置 (左移/右移到头时禁用) */
    tabPos: number;
    /** 编辑器里的字段项 (带数组下标; 分组布局下展示顺序 ≠ 数组顺序) */
    items: { f: F; idx: number }[];
    addField: (tab?: string, sec?: string) => void;
    newTab: () => Promise<void>;
    newSection: (tab: string) => Promise<void>;
    rename: (tab: string, sec?: string) => Promise<void>;
    remove: (tab: string, sec?: string) => Promise<void>;
    moveTab: (dir: -1 | 1) => void;
    moveSection: (tab: string, sec: string, dir: -1 | 1) => void;
    /** 字段上下移的目标下标: 分组布局下只和同组相邻字段换位; 没有返回 -1 */
    neighborOf: (i: number, dir: -1 | 1) => number;
}

export function useLayoutEditor<F extends FieldGroupAttrs>({
    fields,
    setFields,
    makeField,
    setError,
}: LayoutEditorOptions<F>): LayoutEditor<F> {
    const [layout, setLayout] = useState<Layout>(null);
    const [activeTab, setActiveTab] = useState('');
    const [focusTab, setFocusTab] = useState<string | undefined>();
    const [openSection, setOpenSection] = useState<{ key: string }>();
    const items = useMemo(() => fields.map((f, idx) => ({ f, idx })), [fields]);
    const grouped = !!layout && layout.length > 0;
    const tabPos = layout ? layout.findIndex((t) => t.tab === activeTab) : -1;

    const askName = async (message: string, title: string, def = '', placeholder = '') =>
        ((await promptDialog(message, def, { title, placeholder })) || '').trim();

    const addField = (tab?: string, sec?: string) => {
        if (!grouped || tab === undefined || sec === undefined) {
            setFields([...fields, makeField()]);
            return;
        }
        const nf = makeField(tab, sec);
        let at = -1;
        fields.forEach((f, i) => {
            if (sameGroup(f, nf)) at = i;
        });
        const next = [...fields];
        next.splice(at < 0 ? fields.length : at + 1, 0, nf);
        setFields(next);
        setFocusTab(tab);
        setOpenSection({ key: sectionKey(tab, sec) });
    };

    // 页签 / 分组改名: 布局与该组字段的 tab/sec 一起改 (名称必填, 同级不可重名)
    const rename = async (tab: string, sec?: string) => {
        if (!layout) return;
        const isSec = sec !== undefined;
        const cur = isSec ? sec : tab;
        const nv = await askName(isSec ? `分组「${cur}」改名为:` : `页签「${cur}」改名为:`, isSec ? '分组改名' : '页签改名', cur);
        if (!nv || nv === cur) return;
        const t = layout.find((x) => x.tab === tab);
        if (isSec ? t?.sections.some((x) => x.sec === nv) : layout.some((x) => x.tab === nv)) {
            setError(`${isSec ? '分组' : '页签'}「${nv}」已存在`);
            return;
        }
        setError('');
        setLayout(
            layout.map((x) => {
                if (x.tab !== tab) return x;
                if (!isSec) return { ...x, tab: nv };
                return { ...x, sections: x.sections.map((y) => (y.sec === sec ? { sec: nv } : y)) };
            }),
        );
        setFields(
            fields.map((f) => {
                if (norm(f.tab) !== tab) return f;
                if (isSec) return norm(f.sec) === sec ? { ...f, sec: nv } : f;
                return { ...f, tab: nv };
            }),
        );
        if (!isSec) setFocusTab(nv);
    };

    // 新建页签: 平铺字段清单第一次建页签时, 现有字段需要一个落脚的分组, 一并问分组名
    const newTab = async () => {
        const tab = await askName('新建页签, 名称:', '新建页签', '', '如 冷却');
        if (!tab) return;
        if (tab === UNPLACED_TAB || layout?.some((x) => x.tab === tab)) {
            setError(`页签「${tab}」已存在或为保留名`);
            return;
        }
        setError('');
        if (!grouped && fields.length > 0) {
            const sec = await askName(`现有 ${fields.length} 个字段会放进页签「${tab}」下的一个分组, 分组名称:`, '新建分组', '', '如 基础信息');
            if (!sec) return;
            setLayout([{ tab, sections: [{ sec }] }]);
            setFields(fields.map((f) => ({ ...f, tab, sec })));
            setFocusTab(tab);
            return;
        }
        setLayout([...(layout || []), { tab, sections: [] }]);
        setFocusTab(tab);
    };

    // 新建分组 (空分组即可保存, 之后再往里加字段)
    const newSection = async (tab: string) => {
        if (!layout) return;
        const sec = await askName(`在页签「${tab}」里新建分组, 名称:`, '新建分组', '', '如 打印温度');
        if (!sec) return;
        if (layout.find((x) => x.tab === tab)?.sections.some((x) => x.sec === sec)) {
            setError(`分组「${sec}」已存在`);
            return;
        }
        setError('');
        setLayout(layout.map((x) => (x.tab === tab ? { ...x, sections: [...x.sections, { sec }] } : x)));
        setOpenSection({ key: sectionKey(tab, sec) });
    };

    // 删除页签 / 分组 = 连同其中字段一起删
    const remove = async (tab: string, sec?: string) => {
        if (!layout) return;
        const hit = (f: F) => norm(f.tab) === tab && (sec === undefined || norm(f.sec) === sec);
        const n = fields.filter(hit).length;
        const what = sec === undefined ? `页签「${tab}」` : `分组「${sec}」`;
        if (!(await confirmDialog(`确认删除${what}${n > 0 ? `及其中 ${n} 个字段` : ''}? 保存后生效.`))) return;
        const next: LayoutTab[] =
            sec === undefined
                ? layout.filter((x) => x.tab !== tab)
                : layout.map((x) => (x.tab === tab ? { ...x, sections: x.sections.filter((y) => y.sec !== sec) } : x));
        setLayout(next.length > 0 ? next : null);
        setFields(fields.filter((f) => !hit(f)));
    };

    const neighborOf = (i: number, dir: -1 | 1): number => {
        for (let j = i + dir; j >= 0 && j < fields.length; j += dir) {
            if (!grouped || sameGroup(fields[j], fields[i])) return j;
        }
        return -1;
    };

    return {
        layout,
        setLayout,
        grouped,
        activeTab,
        setActiveTab,
        focusTab,
        openSection,
        tabPos,
        items,
        addField,
        newTab,
        newSection,
        rename,
        remove,
        moveTab: (dir) => layout && setLayout(moveTabIn(layout, activeTab, dir)),
        moveSection: (tab, sec, dir) => layout && setLayout(moveSectionIn(layout, tab, sec, dir)),
        neighborOf,
    };
}
